import { useCallback, useEffect, useState } from 'react';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import type { FloorPlanRoomAssignment, FloorPlanRoomOption } from '../models/floorplan-room';
import { getFloorPlanRooms, type FloorPlanRoomsFailure } from '../services/floorplan-editor.service';

export interface FloorPlanRoomsState {
  readonly loading: boolean;
  readonly rooms: FloorPlanRoomOption[];
  readonly assignments: FloorPlanRoomAssignment[];
  /**
   * The office's fill-in period and the earliest date a retirement may be scheduled for, both
   * resolved server-side in the office's booking timezone.
   *
   * Zero and an empty string until the rooms have been read. The dialog that needs them is only
   * reachable from a desk that has an assignment, so it cannot open before they arrive.
   */
  readonly planPeriodDays: number;
  readonly earliestRetirementDate: string;
  /**
   * Why the rooms are missing, when they are. Undefined once they have been read, including
   * when the office genuinely has none — "refused" and "there are none" have to be told
   * apart, because only one of them is the admin's fault to fix.
   */
  readonly error?: FloorPlanRoomsFailure;
  /** Re-reads the rooms, for after a save has created or moved workplaces. */
  readonly refresh: () => void;
}

/**
 * The rooms a desk can be linked to on this floorplan, and where its desks sit today.
 *
 * Fetched per floorplan rather than per office because the desk assignments are what make
 * the answer specific: the rooms happen to be the office's, but which of them each desk
 * belongs to is a property of this plan's workplaces.
 */
export const useFloorPlanRooms = (floorPlanId: string | undefined): FloorPlanRoomsState => {
  const keycloak = useKeycloak();
  const [rooms, setRooms] = useState<FloorPlanRoomOption[]>([]);
  const [assignments, setAssignments] = useState<FloorPlanRoomAssignment[]>([]);
  const [planPeriodDays, setPlanPeriodDays] = useState(0);
  const [earliestRetirementDate, setEarliestRetirementDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<FloorPlanRoomsFailure | undefined>();
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!floorPlanId) {
      setRooms([]);
      setAssignments([]);
      setPlanPeriodDays(0);
      setEarliestRetirementDate('');
      setError(undefined);
      return undefined;
    }

    let active = true;
    setLoading(true);

    getFloorPlanRooms(floorPlanId, keycloak)
      .then((result) => {
        if (!active) return;

        if (result.status !== 'ok') {
          // Still emptied, so nothing downstream can claim a desk is bookable on data that
          // was never read — but the reason is kept, so the editor can say what happened
          // instead of showing an office that appears to have no rooms and no bookable desks.
          setRooms([]);
          setAssignments([]);
          setPlanPeriodDays(0);
          setEarliestRetirementDate('');
          setError(result.status);
          return;
        }

        setRooms(result.rooms);
        setAssignments(result.assignments);
        setPlanPeriodDays(result.planPeriodDays ?? 0);
        setEarliestRetirementDate(result.earliestRetirementDate ?? '');
        setError(undefined);
      })
      .catch(() => {
        if (!active) return;

        setRooms([]);
        setAssignments([]);
        setPlanPeriodDays(0);
        setEarliestRetirementDate('');
        setError('failed');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [floorPlanId, keycloak, reloadToken]);

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

  return { loading, rooms, assignments, planPeriodDays, earliestRetirementDate, error, refresh };
};

export default useFloorPlanRooms;
